import { useState } from 'react';
import { Loader2, Save, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { adminAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

export const AutoCloseReadySettings = ({ settings }) => {
  const [enabled, setEnabled] = useState(settings.auto_close_ready_enabled !== false);
  const [days, setDays] = useState(String(settings.auto_close_ready_days ?? 14));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings({
        auto_close_ready_enabled: enabled,
        auto_close_ready_days: Math.max(1, parseInt(days, 10) || 14),
      });
      toast.success('Auto-close settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="auto-close-ready-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-teal/10"><Archive className="h-5 w-5 text-teal" /></div>
        <div>
          <h2 className="font-heading text-xl font-medium text-foreground">Auto-Close Collected Orders</h2>
          <p className="font-body text-xs text-foreground/60">Close paid orders left at Ready for Collection</p>
        </div>
      </div>
      <div className="flex items-center justify-between mb-5">
        <div className="pr-4">
          <Label className="font-body text-sm text-foreground font-medium">Enable auto-close</Label>
          <p className="font-body text-xs text-foreground/50 mt-1">
            When ON, paid orders that have been Ready for Collection for the number of days below are moved to Closed. No customer message is sent; the order history records "Auto-closed". Checked hourly.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="toggle-auto-close-ready" />
          <span className="font-body text-xs text-foreground/60">{enabled ? 'On' : 'Off'}</span>
        </div>
      </div>
      <div className="max-w-xs mb-5">
        <Label className="font-body text-sm text-foreground font-medium">Days at Ready before closing</Label>
        <Input
          type="number"
          min="1"
          data-testid="auto-close-ready-days-input"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          disabled={!enabled}
          className="rounded-xl bg-muted border-border font-body mt-2"
        />
        <p className="font-body text-xs text-foreground/50 mt-2">Default 14 days.</p>
      </div>
      <Button
        onClick={save}
        disabled={saving}
        data-testid="save-auto-close-ready-btn"
        className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save Auto-Close Settings
      </Button>
    </div>
  );
};
